// COGS explorer — pull a user's real spend from the backend and normalize it into
// the CogsUsageRow shape the measured view consumes.
//
// Two sources, joined by log_id (backend is renaming `id` → `log_id`; we read
// `log_id ?? id`):
//   - get_token_usage (3 canonical names) → exact per-call tokens for Gemini,
//     Claude, and OpenAI embeddings. One call per name, no pagination.
//   - get_api_logs (paged 10/row) → every call's api_url (action classifier) plus
//     the providers token_usage doesn't cover: Perplexity (tokens live in the
//     response body), Apollo (per-call), ElevenLabs (per input-char).

import { adminService, TOKEN_USAGE_API_NAMES } from '@/api/services/admin';
import type { ApiLogRow, TokenUsageRow } from '@/api/services/admin';
import type { CogsUsageRow } from './compute';

const MAX_PAGES = 40; // safety cap; api_logs bodies can be large (TTS audio)

export interface SyncMeta {
  userId: string;
  pages: number;
  logRows: number;
  keptRows: number;
  tokenMatched: number; // calls whose tokens came from get_token_usage
  truncated: boolean; // hit MAX_PAGES or a mid-page error
}

const logId = (r: { log_id?: number; id?: number }): number | undefined => r.log_id ?? r.id;
const num = (v?: string): number => (v ? parseInt(v, 10) || 0 : 0);

// Provider-specific token extraction from a get_token_usage row.
function tokensFromUsageRow(apiName: string, r: TokenUsageRow): { input: number; output: number } {
  if (apiName.startsWith('gemini')) {
    // thoughts (thinking) tokens bill as output alongside candidates.
    return { input: num(r.prompt_token_count), output: num(r.candidates_token_count) + num(r.thoughts_token_count) };
  }
  if (apiName.startsWith('claude')) {
    return { input: num(r.input_tokens), output: num(r.output_tokens) };
  }
  // embeddings
  return { input: num(r.prompt_tokens) || num(r.total_tokens), output: 0 };
}

// Fallback: parse a provider `usage` block out of api_response (Perplexity; also
// Claude/OpenAI as redundancy). Returns null for plaintext/binary bodies.
function tokensFromResponse(row: ApiLogRow): { input: number; output: number } | null {
  const s = (row.api_response || '').trim();
  if (!(s.startsWith('{') || s.startsWith('['))) return null;
  if (s.length > 400_000) return null; // skip huge/binary (TTS audio)
  try {
    const o = JSON.parse(s);
    const u = o?.usage;
    if (u && typeof u === 'object') {
      if ('input_tokens' in u) return { input: u.input_tokens || 0, output: u.output_tokens || 0 }; // claude
      if ('prompt_tokens' in u) return { input: u.prompt_tokens || 0, output: u.completion_tokens || 0 }; // perplexity/openai chat
    }
  } catch {
    /* not JSON */
  }
  return null;
}

// Map api_url → the action + swappable slot it belongs to.
function classify(row: ApiLogRow): { action: string; slot: string; grounded: boolean } | null {
  const u = row.api_url || '';
  const req = row.api_request || '';
  if (u.includes('generate_content')) {
    // Gemini: message-gen prompts start "Based on…"; everything else is research.
    return req.startsWith('Based on')
      ? { action: 'generate_prospect_messages', slot: 'message_llm', grounded: true }
      : { action: 'prospect_research', slot: 'research_llm', grounded: true };
  }
  if (u.includes('anthropicClient.messages')) return { action: 'coaching_turn', slot: 'coaching_llm', grounded: false };
  if (u.includes('embeddings.create')) return { action: 'coaching_turn', slot: 'rag_embedding', grounded: false };
  if (u.includes('perplexity')) return { action: 'prospect_research', slot: 'research_llm', grounded: true };
  if (u.includes('apollo.io')) return { action: 'enrich_prospect', slot: 'enrichment', grounded: false };
  if (u.includes('elevenlabs')) return { action: 'coaching_turn', slot: 'tts', grounded: false };
  return null;
}

export async function fetchUserUsage(userId: string): Promise<{ rows: CogsUsageRow[]; meta: SyncMeta }> {
  // 1. Per-call tokens keyed by log_id, from get_token_usage (3 names).
  const tokenById = new Map<number, { input: number; output: number }>();
  await Promise.all(
    TOKEN_USAGE_API_NAMES.map(async (name) => {
      try {
        const res = await adminService.getTokenUsage(userId, name);
        for (const r of res.data || []) {
          const id = logId(r);
          if (id != null) tokenById.set(id, tokensFromUsageRow(name, r));
        }
      } catch {
        // endpoint has been observed to 500 on users with no rows for a name — skip.
      }
    }),
  );

  // 2. Page api_logs.
  const logs: ApiLogRow[] = [];
  let offset = 0;
  let pages = 0;
  let truncated = false;
  for (; pages < MAX_PAGES; pages++) {
    let batch: ApiLogRow[];
    try {
      batch = await adminService.getApiLogs(userId, offset);
    } catch {
      truncated = true;
      break;
    }
    if (!Array.isArray(batch) || batch.length === 0) break;
    logs.push(...batch);
    offset += batch.length;
    if (batch.length < 10) break; // last page
  }
  if (pages >= MAX_PAGES) truncated = true;

  // 3. Normalize each call into a CogsUsageRow.
  const rows: CogsUsageRow[] = [];
  let tokenMatched = 0;
  for (const r of logs) {
    const c = classify(r);
    if (!c) continue;
    const row: CogsUsageRow = {
      user_id: userId,
      action: c.action,
      slot: c.slot,
      grounded: c.grounded,
      model: r.api_name,
      calls: 1,
    };

    if (c.slot === 'enrichment') {
      // Apollo: per-call, no tokens.
    } else if (c.slot === 'tts') {
      // ElevenLabs: cost is per input character (the text synthesized).
      row.chars = (r.api_request || '').length;
    } else {
      const id = logId(r);
      const tk = id != null ? tokenById.get(id) : undefined;
      if (tk) {
        row.input_tokens = tk.input;
        row.output_tokens = tk.output;
        tokenMatched++;
      } else {
        const fromResp = tokensFromResponse(r);
        if (fromResp) {
          row.input_tokens = fromResp.input;
          row.output_tokens = fromResp.output;
        } else {
          // Last resort (e.g. Gemini rows with no token_usage match yet): estimate
          // from character length at ~4 chars/token.
          row.input_tokens = Math.round((r.api_request || '').length / 4);
          row.output_tokens = Math.round((r.api_response || '').length / 4);
        }
      }
    }
    rows.push(row);
  }

  return {
    rows,
    meta: { userId, pages, logRows: logs.length, keptRows: rows.length, tokenMatched, truncated },
  };
}
